/* eslint-disable complexity */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { masks, validations } from 'src/components/Auth/_lib/validations';
import { Input } from 'src/components/Common/@electron/Input';
import SvgLoader from 'src/components/Common/SvgLoader';
import Select from 'src/components/Microsite/components/Select';
import { ContactFormInput, ContactFormProps } from '../../types';
import { FormStepButtons } from '../Buttons';

const ContactForm = ({
  props,
  handleCancel,
  handleContinue,
  handlePrevious,
  defaultValues,
  rebateDetails,
}: ContactFormProps) => {
  const [formattedPhone, setFormattedPhone] = useState(defaultValues?.phone || '');
  const { register, handleSubmit, formState, getValues, setValue } = useForm<ContactFormInput>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: defaultValues || {},
  });
  const { errors } = formState;

  const getDataFromValues = () => {
    return {
      firstName: getValues('firstName') || '',
      lastName: getValues('lastName') || '',
      streetAddress1: getValues('streetAddress1') || '',
      streetAddress2: getValues('streetAddress2') || '',
      stateCode: getValues('stateCode') || '',
      city: getValues('city') || '',
      zip: getValues('zip') || '',
      phone: getValues('phone') || '',
      email: getValues('email') || '',
      offerCode: getValues('offerCode') || '',
    } as ContactFormInput;
  };

  const onSubmit = () => {
    const data = getDataFromValues();
    handleContinue(data);
  };

  const onPrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    const data = getDataFromValues();
    handlePrevious(data);
  };

  const onChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target?.value;
    if (val) {
      // removes first character if it is a 1 (then can use autocomplete)
      const noLeading1Val = val.replace(/^(1)/, '').replace('(1', '(');
      const maskedVal = masks.tel(noLeading1Val);
      setValue('phone', maskedVal);
      setFormattedPhone(maskedVal);
    } else {
      setValue('phone', '');
      setFormattedPhone('');
    }
  };

  return (
    <form
      data-testid="find-it-duke-contact-form"
      className="w-full md:max-w-[600px] bg-white rounded-md p-16"
      noValidate
    >
      <div className="space-y-16 mb-24">
        <h3>{props.formTitle}</h3>
        <div className="sm:flex sm:flex-row sm:space-x-16 space-y-16 sm:space-y-0">
          <div className="grow">
            <Input
              type="text"
              id="firstName"
              name="firstName"
              label={props.firstNameLabel}
              status={errors.firstName ? 'error' : undefined}
              errorMessage={errors.firstName?.message}
              required={props.firstNameRequired}
              register={register('firstName', {
                required: {
                  value: props.firstNameRequired,
                  message: props.firstNameRequiredErrorMessage || '',
                },
              })}
            />
          </div>
          <div className="grow">
            <Input
              type="text"
              id="lastName"
              name="lastName"
              label={props.lastNameLabel}
              status={errors.lastName ? 'error' : undefined}
              errorMessage={errors.lastName?.message}
              required={props.lastNameRequired}
              register={register('lastName', {
                required: {
                  value: props.lastNameRequired,
                  message: props.lastNameRequiredErrorMessage || '',
                },
              })}
            />
          </div>
        </div>
        <Input
          type="text"
          id="streetAddress1"
          name="streetAddress1"
          label={props.streetAddress1Label}
          status={errors.streetAddress1 ? 'error' : undefined}
          errorMessage={errors.streetAddress1?.message}
          required={props.streetAddress1Required}
          register={register('streetAddress1', {
            required: {
              value: props.streetAddress1Required,
              message: props.streetAddress1RequiredErrorMessage || '',
            },
          })}
        />
        <Input
          type="text"
          id="streetAddress2"
          name="streetAddress2"
          label={props.streetAddress2Label}
          required={props.streetAddress2Required}
          errorMessage={errors.streetAddress2?.message}
          register={register('streetAddress2', {
            required: {
              value: props.streetAddress2Required,
              message: props.streetAddress2RequiredErrorMessage || '',
            },
          })}
        />
        <Input
          type="text"
          id="city"
          name="city"
          label={props.cityLabel}
          status={errors.city ? 'error' : undefined}
          errorMessage={errors.city?.message}
          required={props.cityRequired}
          register={register('city', {
            required: { value: props.cityRequired, message: props.cityRequiredErrorMessage || '' },
          })}
        />
        <div className="sm:flex sm:flex-row sm:space-x-16 space-y-16 sm:space-y-0">
          <div className="grow">
            <Select
              id="stateSelect"
              name="stateSelect"
              label={props.stateSelectLabel}
              status={errors.stateCode ? 'error' : undefined}
              required={props.stateSelectRequired}
              register={register('stateCode', {
                required: {
                  value: props.stateSelectRequired,
                  message: props.stateSelectRequiredErrorMessage || '',
                },
              })}
              errorMessage={errors.stateCode?.message}
              list={props.stateSelectList}
            />
          </div>
          <div className="grow">
            <Input
              type="text"
              inputMode="numeric"
              id="zip"
              name="zip"
              label={props.zipCodeLabel}
              status={errors.zip ? 'error' : undefined}
              required={props.zipCodeRequired}
              errorMessage={errors.zip?.message}
              register={register('zip', {
                required: {
                  value: props.zipCodeRequired,
                  message: props.zipCodeRequiredErrorMessage || '',
                },
                pattern: {
                  value: /^\d{5}(?:[-\s]\d{4})?$/,
                  message: props.zipCodeValidErrorMessage || '',
                },
              })}
            />
          </div>
        </div>
        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          id="phone"
          label={props.phoneLabel}
          status={errors.phone ? 'error' : undefined}
          required={props.phoneRequired}
          errorMessage={errors.phone?.message}
          value={formattedPhone}
          {...register('phone', {
            required: {
              value: props.phoneRequired,
              message: props.phoneRequiredErrorMessage || '',
            },
            pattern: {
              value: validations.phone.value,
              message: props.phoneValidErrorMessage || '',
            },
          })}
          onChange={onChangePhone}
        />
        <Input
          type="email"
          autoComplete="email"
          id="email"
          name="email"
          label={props.emailLabel}
          status={errors.email ? 'error' : undefined}
          required={props.emailRequired}
          errorMessage={errors.email?.message}
          register={register('email', {
            required: {
              value: props.emailRequired,
              message: props.emailRequiredErrorMessage || '',
            },
            pattern: {
              value: validations.email.value,
              message: props.emailValidErrorMessage || '',
            },
          })}
        />
      </div>
      <div className="space-y-16">
        <h3 className="">{props.offerSectionTitle}</h3>
        <Input
          type="text"
          id="offerCode"
          name="offerCode"
          label={props.offerCodeLabel}
          required={props.offerCodeRequired}
          errorMessage={errors.offerCode?.message}
          register={register('offerCode', {
            required: {
              value: props.offerCodeRequired,
              message: props.offerCodeRequiredErrorMessage || '',
            },
          })}
        />
        {!!rebateDetails?.name && (
          <div className="flex flex-row gap-4">
            <div className="rounded-full icon-20 p-4 bg-green-dark">
              <SvgLoader name="Check" color="text-white" size={13} />
            </div>
            <p>
              Up to ${rebateDetails.amount} in {rebateDetails.name} rebates selected
            </p>
          </div>
        )}
        <FormStepButtons
          handlePreviousClick={onPrevious}
          handleContinueClick={handleSubmit(onSubmit)}
          handleCancelClick={handleCancel}
          continueButtonText={props.continueButtonText}
          cancelButtonText={props.cancelButtonText}
          previousButtonText={props.previousButtonText}
        />
      </div>
    </form>
  );
};

export default ContactForm;
